import { OPERATOR_HEARTBEAT_INTERVAL_MS } from '@groupe-reca/contracts'
import type { GeoFix } from '@/platform/geolocation'
import type { MissionRepository, PresencePosition } from '../MissionRepository'

export interface PresenceState {
  /** null tant qu'aucun battement n'a abouti. */
  lastBeatAt: number | null
  /** Le dernier battement a échoué (réseau) : le centre verra « Connexion interrompue » après 90 s. */
  failing: boolean
  /** Déclaré par l'opérateur, tel que le serveur l'a enregistré. */
  availableForHelp: boolean
}

export interface PresenceClock {
  now: () => number
  setInterval: (fn: () => void, ms: number) => unknown
  clearInterval: (handle: unknown) => void
  uuid: () => string
}

const browserClock: PresenceClock = {
  now: () => Date.now(),
  setInterval: (fn, ms) => setInterval(fn, ms),
  clearInterval: (handle) => {
    clearInterval(handle as ReturnType<typeof setInterval>)
  },
  uuid: () => crypto.randomUUID(),
}

/** Mesure réelle du port GPS, avec son heure de capture (jamais redatée). */
export function presencePosition(fix: GeoFix | null): PresencePosition | null {
  if (!fix) return null
  return {
    latitude: fix.latitude,
    longitude: fix.longitude,
    accuracy: fix.accuracy,
    heading: fix.heading,
    speed: fix.speed,
    capturedAt: new Date(fix.timestamp).toISOString(),
  }
}

/**
 * Présence de la tablette (ADR-019) : un battement toutes les 30 s, avec ou sans mission, joint de la dernière mesure
 * GPS connue. Rien n'est mis en file : un battement manqué est simplement manqué (le centre affiche la connexion
 * interrompue). Une session par ouverture de l'application; la déconnexion explicite retire le repère du centre.
 */
export class PresenceBeat {
  readonly sessionId: string
  private sequence = 0
  private timer: unknown = null
  private inFlight = false
  private stopped = false
  private state: PresenceState = { lastBeatAt: null, failing: false, availableForHelp: false }
  private listeners = new Set<(state: PresenceState) => void>()

  constructor(
    private readonly repository: Pick<MissionRepository, 'heartbeat' | 'disconnect'>,
    private readonly latestFix: () => GeoFix | null,
    private readonly clock: PresenceClock = browserClock,
  ) {
    this.sessionId = clock.uuid()
  }

  get current(): PresenceState {
    return this.state
  }

  subscribe(listener: (state: PresenceState) => void): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  start(): void {
    if (this.timer !== null) return
    this.stopped = false
    void this.beat()
    this.timer = this.clock.setInterval(() => {
      void this.beat()
    }, OPERATOR_HEARTBEAT_INTERVAL_MS)
  }

  stop(): void {
    this.stopped = true
    if (this.timer !== null) this.clock.clearInterval(this.timer)
    this.timer = null
  }

  async beat(): Promise<void> {
    if (this.inFlight || this.stopped) return
    this.inFlight = true
    this.sequence += 1
    const position = presencePosition(this.latestFix())
    try {
      const result = await this.repository.heartbeat({
        sessionId: this.sessionId,
        sequence: this.sequence,
        ...(position ? { position } : {}),
      })
      this.update({ lastBeatAt: this.clock.now(), failing: false, availableForHelp: result.availableForHelp })
    } catch {
      this.update({ ...this.state, failing: true })
    } finally {
      this.inFlight = false
    }
  }

  /** Disponibilité confirmée par le serveur (`set_operator_help_availability_v1`). */
  setAvailable(available: boolean): void {
    this.update({ ...this.state, availableForHelp: available })
  }

  /** Déconnexion explicite (bouton « Se déconnecter ») : au mieux, sans bloquer la sortie hors ligne. */
  async disconnect(): Promise<void> {
    this.stop()
    try {
      await this.repository.disconnect({ sessionId: this.sessionId })
    } catch {
      // Hors ligne : le centre passera de lui-même à « Connexion interrompue » puis « Session expirée ».
    }
    this.update({ ...this.state, availableForHelp: false })
  }

  private update(next: PresenceState): void {
    this.state = next
    for (const listener of this.listeners) listener(next)
  }
}
